import React from 'react';
import {
  Body1,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
} from '@fluentui/react-components';
import {
  IconButton,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  DefaultButton,
  Icon,
} from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import { InstructionsContainer, H1, H2, P } from '../Pages-styles';
import { usePageContext } from '../../hooks/PageContext';
import { Attachment } from '../../graphql/types';

export const BlogView: React.FC = () => {
  const { actions, isAuthenticated } = usePageContext();
  const navigate = useNavigate();

  const {
    currentNote,
    setMode,
    setTagFilter,
    setSearch,
    handleToggleFavorite,
    isAdmin,
    setForm,
    setCurrentNote,
    setIsCreating,
    confirmAndDeleteNote,
    confirmDeleteId,
    setConfirmDeleteId,
    handleDeleteNote,
  } = actions;

  const API_URL = process.env.REACT_APP_API_URL || 'https://sd.smetaforte.ru/api';

  if (!currentNote) return null;

  return (
    <>
      <Dialog
        open={confirmDeleteId !== null}
        onOpenChange={(_: any, data: any) => {
          if (!data.open) setConfirmDeleteId(null);
        }}
        modalType="modal"
      >
        <DialogSurface
          style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}
        >
          <DialogBody>
            <DialogTitle>Удалить статью?</DialogTitle>
            <DialogContent>
              Вы уверены, что хотите удалить эту статью? Это действие нельзя отменить.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmDeleteId(null)}>
                Отмена
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  if (confirmDeleteId) {
                    handleDeleteNote(confirmDeleteId);
                    setConfirmDeleteId(null);
                  }
                }}
              >
                Удалить
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <InstructionsContainer>
        <IconButton
          iconProps={{ iconName: 'Back' }}
          title="Назад к списку"
          onClick={() => {
            setMode('list');
            navigate('/blog', { replace: true });
          }}
          style={{ marginBottom: '20px' }}
        />
        <H1>{currentNote.title}</H1>
        <P>
          <em>{new Date(currentNote.createdAt).toLocaleDateString()}</em>
        </P>

        {currentNote.image && currentNote.image !== currentNote.previewImage && (
          <img
            src={currentNote.image}
            alt={currentNote.title}
            style={{
              width: '100%',
              maxHeight: '400px',
              objectFit: 'cover',
              borderRadius: '4px',
              marginBottom: '20px',
            }}
          />
        )}

        <div
          dangerouslySetInnerHTML={{ __html: currentNote.content }}
          style={{ lineHeight: '1.6', fontSize: '14px', color: '#333' }}
        />

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '12px 0' }}>
          {currentNote.tags.map((tag: string) => (
            <span
              key={tag}
              style={{
                color: '#0078d4',
                fontWeight: 500,
                fontSize: '14px',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setMode('list');
                setTagFilter(tag);
                setSearch('');
              }}
            >
              #{tag}
            </span>
          ))}
        </div>

        {isAuthenticated && (
          <IconButton
            iconProps={{
              iconName: 'FavoriteStar',
              style: currentNote.favoritedBy.some((u: { id: string }) => u.id === actions.user?.id)
                ? { color: 'gold' }
                : { color: 'inherit' },
            }}
            title={
              currentNote.favoritedBy.some((u: { id: string }) => u.id === actions.user?.id)
                ? 'Убрать из избранного'
                : 'Добавить в избранное'
            }
            onClick={() => handleToggleFavorite(currentNote.id)}
            style={{ marginTop: '16px' }}
          />
        )}

        {currentNote.attachments && currentNote.attachments.length > 0 && (
          <div style={{ marginTop: '32px' }}>
            <H2>Вложения</H2>
            <DetailsList
              items={currentNote.attachments}
              columns={[
                {
                  key: 'column-file',
                  name: 'Файл',
                  fieldName: 'filename',
                  minWidth: 200,
                  maxWidth: 300,
                  isRowHeader: true,
                  isResizable: true,
                  onRender: (item: Attachment) => (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Icon iconName="Page" styles={{ root: { marginRight: 8 } }} />
                      <Body1>{item.filename}</Body1>
                    </div>
                  ),
                },
                {
                  key: 'column-size',
                  name: 'Размер',
                  fieldName: 'size',
                  minWidth: 100,
                  maxWidth: 120,
                  isResizable: true,
                  onRender: (item: any) => {
                    const sizeInBytes = item.size;
                    const size =
                      sizeInBytes < 1024 * 1024
                        ? (sizeInBytes / 1024).toFixed(1) + ' KB'
                        : (sizeInBytes / (1024 * 1024)).toFixed(1) + ' MB';
                    return <Body1>{size}</Body1>;
                  },
                },
                {
                  key: 'column-download',
                  name: 'Скачать',
                  fieldName: 'download',
                  minWidth: 120,
                  maxWidth: 140,
                  isResizable: false,
                  onRender: (item: Attachment) => (
                    <a
                      href={`${API_URL}/attachments/${item.id}`}
                      download={item.filename}
                      style={{ textDecoration: 'none' }}
                    >
                      <DefaultButton text="Скачать" />
                    </a>
                  ),
                },
              ]}
              layoutMode={DetailsListLayoutMode.justified}
              selectionMode={SelectionMode.none}
              onRenderRow={(props, defaultRender) => (
                <div style={{ display: 'table-row', width: '100%' }}>
                  {defaultRender?.(props)}
                </div>
              )}
              compact={false}
              styles={{ root: { tableLayout: 'fixed', width: '100%' } }}
            />
          </div>
        )}

        {isAdmin && (
          <div style={{ marginTop: '24px', display: 'flex', gap: '10px' }}>
            <IconButton
              iconProps={{ iconName: 'Edit' }}
              title="Редактировать"
              onClick={() => {
                setForm({
                  title: currentNote.title,
                  content: currentNote.content,
                  image: currentNote.image || '',
                  previewImage: currentNote.previewImage || '',
                  tags: currentNote.tags.join(', '),
                  attachments: currentNote.attachments || [],
                });
                setCurrentNote(currentNote);
                setIsCreating(false);
                setMode('edit');
              }}
            />
            <IconButton
              iconProps={{ iconName: 'Delete' }}
              title="Удалить"
              onClick={() => confirmAndDeleteNote(currentNote.id)}
            />
          </div>
        )}
      </InstructionsContainer>
    </>
  );
};